import { toast } from '@/components/ui/toaster'
import { useClearMetadataMutation } from '@/hooks/api/use-clear-metadata-mutation'
import { TableDetailsQueryParams } from '@/hooks/api/use-table-details-query'
import { Table, TablesQueryParams } from '@/hooks/api/use-table-query'
import React from 'react'
import {
  ENRICHMENT_AGENTS,
  ENRICHMENT_FIELD_MAPPINGS,
  ENRICHMENT_METADATA_TYPES,
  ENRICHMENT_TOOL_IDS,
  ENRICHMENT_TYPES,
  EnrichmentFieldValue,
  EnrichmentToolId,
  EnrichmentType,
} from '../constants'
import { EnrichmentControlsState, EnrichmentHandlers, EnrichmentWorkflow } from '../types'
import { getAgentLabel } from '../utils'
import { useSaveMetadata } from './use-save-metadata'

export const useEnrichmentHandlers = (
  type: EnrichmentType,
  enrichmentWorkflow: EnrichmentWorkflow,
  query: any,
  setEnrichmentState: React.Dispatch<React.SetStateAction<EnrichmentControlsState>>,
  queryParams: TablesQueryParams | TableDetailsQueryParams,
  enrichmentState: EnrichmentControlsState,
  customRefetch?: () => Promise<void>,
  refetchScoreQueries?: () => void,
): EnrichmentHandlers => {
  // Type guard to check if queryParams has table_name (TableDetailsQueryParams)
  const isTableDetails = (
    params: TablesQueryParams | TableDetailsQueryParams,
  ): params is TableDetailsQueryParams => {
    return 'table_name' in params
  }
  const selectedTableIds = enrichmentWorkflow.state.selectedTableIds || []

  // Mutations
  const clearMetadataMutation = useClearMetadataMutation()
  const saveMetadata = useSaveMetadata({
    queryParams,
    context: 'enrichment',
    selectedMetadata: enrichmentWorkflow.state.selectedMetadata,
    refetchScoreQueries,
    onSuccess: () => {
      // Use custom refetch function if provided (for metadata table with version sequence)
      if (customRefetch) {
        customRefetch().finally(() => {
          // Clean up enrichment state after refetch completes
          handleCancel()
        })
      } else {
        query.refetch().finally(() => {
          handleCancel()
        })
      }
    },
  })

  // Local state
  const [isClearingMetadata, setIsClearingMetadata] = React.useState(false)

  const handleEnrich = React.useCallback(() => {
    if (type === ENRICHMENT_TYPES.TABLE) {
      if (selectedTableIds.length === 0) {
        toast.error('Please select at least one table to enrich')
        return
      }

      enrichmentWorkflow.startEnrichment(
        {
          service_name: queryParams.service_name,
          database_name: queryParams.database_name,
          tables: selectedTableIds,
          enrichment_fields: [...ENRICHMENT_FIELD_MAPPINGS.TABLE_DESCRIPTION],
          industry: getAgentLabel(enrichmentState.selectedAgent),
          tool_id: ENRICHMENT_TOOL_IDS.GENERATE_TABLE_DESCRIPTION,
        },
        enrichmentWorkflow.state.selectedMetadata,
      )
    } else {
      let tool_id: EnrichmentToolId
      let enrichment_fields: EnrichmentFieldValue[]

      if (enrichmentWorkflow.state.selectedMetadata?.includes(ENRICHMENT_METADATA_TYPES.PII_TAGS)) {
        tool_id = ENRICHMENT_TOOL_IDS.TAG_GENERATOR
        enrichment_fields = [...ENRICHMENT_FIELD_MAPPINGS.PII_TAGS]
      } else if (
        enrichmentWorkflow.state.selectedMetadata?.includes(
          ENRICHMENT_METADATA_TYPES.DATA_QUALITY_TAGS,
        )
      ) {
        tool_id = ENRICHMENT_TOOL_IDS.DQ_TAG_GENERATOR
        enrichment_fields = [...ENRICHMENT_FIELD_MAPPINGS.DATA_QUALITY_TAGS]
      } else if (
        enrichmentWorkflow.state.selectedMetadata?.includes(
          ENRICHMENT_METADATA_TYPES.SENSITIVITY_TAGS,
        )
      ) {
        tool_id = ENRICHMENT_TOOL_IDS.SENSITIVITY_TAG_GENERATOR
        enrichment_fields = [...ENRICHMENT_FIELD_MAPPINGS.SENSITIVITY_TAGS]
      } else if (
        enrichmentWorkflow.state.selectedMetadata?.includes(ENRICHMENT_METADATA_TYPES.DQ_RULES)
      ) {
        tool_id = ENRICHMENT_TOOL_IDS.GENERATE_DQ_RULES
        enrichment_fields = [...ENRICHMENT_FIELD_MAPPINGS.DQ_RULES]
      } else {
        tool_id = ENRICHMENT_TOOL_IDS.ENRICH_METADATA
        enrichment_fields = [...ENRICHMENT_FIELD_MAPPINGS.COLUMN_METADATA]
      }

      if (!isTableDetails(queryParams)) {
        console.error('Table name is required for column enrichment')
        return
      }

      enrichmentWorkflow.startEnrichment(
        {
          service_name: queryParams.service_name,
          database_name: queryParams.database_name,
          tables: [queryParams.table_name],
          enrichment_fields: enrichment_fields,
          industry: getAgentLabel(enrichmentState.selectedAgent),
          tool_id,
        },
        enrichmentWorkflow.state.selectedMetadata,
      )
    }
  }, [
    type,
    selectedTableIds,
    query.data,
    enrichmentWorkflow,
    queryParams,
    enrichmentWorkflow.state.selectedMetadata || [],
    enrichmentState.selectedAgent,
  ])

  const handleSaveTableMetadata = async (tablesToSave?: any[]) => {
    const enrichedTables = tablesToSave || enrichmentWorkflow.state.enrichmentData.tables
    const filteredTables = enrichedTables.filter((table: Table) =>
      selectedTableIds.includes(table.table_name || (table as any).tableName),
    )

    const saveData = saveMetadata.convertEnrichmentTableData(filteredTables)
    await saveMetadata.saveTableDescriptions(saveData)
    // handleCancel() will be called by onSuccess callback after refetch completes
  }

  const handleSaveColumnData = async (columnsToSave?: any[]) => {
    const enrichedColumns =
      columnsToSave || enrichmentWorkflow.state.enrichmentData.tables[0]?.columns
    if (!enrichedColumns) {
      console.error('No enriched data to save')
      return
    }

    const saveData = saveMetadata.convertEnrichmentColumnData(enrichedColumns)
    await saveMetadata.saveColumnData(saveData)
    // handleCancel() will be called by onSuccess callback after refetch completes
  }

  const saveData = React.useCallback(
    async (tables: any[], errorMessage: string) => {
      if (!tables) return

      try {
        if (type === 'table') {
          await handleSaveTableMetadata(tables)
        } else {
          await handleSaveColumnData(tables[0]?.columns)
        }
      } catch (error) {
        console.error(error)
        toast.error(errorMessage)
      }
    },
    [type, handleSaveTableMetadata, handleSaveColumnData],
  )

  const handleSave = React.useCallback(async () => {
    const currentView = enrichmentWorkflow.state.currentView

    if (currentView === 'original') {
      await saveData(enrichmentWorkflow.state.originalData?.tables, 'Error saving original data')
    } else {
      await saveData(enrichmentWorkflow.state.enrichmentData?.tables, 'Error saving enriched data')
    }
  }, [
    saveData,
    enrichmentWorkflow.state.enrichmentData,
    enrichmentWorkflow.state.originalData,
    enrichmentWorkflow.state.currentView,
  ])

  const handleUndoEnrichment = React.useCallback(() => {
    // Switch to original view instead of clearing enrichment data
    enrichmentWorkflow.switchToOriginalView()
  }, [enrichmentWorkflow])

  const handleRedoEnrichment = React.useCallback(() => {
    // Switch back to enriched view
    enrichmentWorkflow.switchToEnrichedView()
  }, [enrichmentWorkflow])

  const handleCancel = React.useCallback(() => {
    enrichmentWorkflow.clearAllData()
    // Always reset enrichment state to initial values
    setEnrichmentState({
      selectedAgent: ENRICHMENT_AGENTS.EM_AGENTS,
      showEnrichmentControls: false,
    })

    if (type === ENRICHMENT_TYPES.TABLE) {
      enrichmentWorkflow.updateSelectedTableIds([])
      enrichmentWorkflow.updateSelectedMetadata([ENRICHMENT_METADATA_TYPES.TABLE_DESCRIPTION])
    } else {
      enrichmentWorkflow.updateSelectedMetadata([ENRICHMENT_METADATA_TYPES.COLUMN_DESCRIPTION])
    }
  }, [enrichmentWorkflow, setEnrichmentState, type])

  // Helper function to create clear parameters for table metadata
  const createTableClearParams = React.useCallback(
    (tableNames: string[]) => ({
      service_name: queryParams.service_name,
      database_name: queryParams.database_name,
      schema_name: queryParams.schema_name,
      tables: tableNames.map((tableName: string) => ({
        table_name: tableName,
        clear_table_description: true,
        clear_column_metadata: false,
        clear_column_tags: false,
      })),
    }),
    [queryParams],
  )

  // Helper function to create clear parameters for column metadata
  const createColumnClearParams = React.useCallback(
    () => ({
      service_name: queryParams.service_name,
      database_name: queryParams.database_name,
      schema_name: queryParams.schema_name,
      tables: [
        {
          table_name: (queryParams as TableDetailsQueryParams).table_name,
          clear_table_description: false,
          clear_column_metadata: true,
          clear_column_tags: true,
        },
      ],
    }),
    [queryParams],
  )

  // Helper function to handle refetch after successful clear
  const handleRefetchAfterClear = React.useCallback(async () => {
    if (customRefetch) {
      await customRefetch()
    } else {
      await query.refetch()
    }
    // Refetch score queries after clearing metadata
    refetchScoreQueries?.()
  }, [customRefetch, query, refetchScoreQueries])

  // Helper function to clear table metadata
  const clearTableMetadata = React.useCallback(async () => {
    const allTableNames = query.data.tables?.map((table: Table) => table.table_name) || []

    if (allTableNames.length === 0) {
      toast.error('No tables found to clear metadata from')
      return false
    }

    const clearParams = createTableClearParams(allTableNames)
    const result = await clearMetadataMutation.mutateAsync(clearParams)

    if (result) {
      toast.success('Table metadata cleared successfully for all tables')
      await handleRefetchAfterClear()
      return true
    } else {
      toast.error('Failed to clear table metadata')
      console.error('Clear failed:', result)
      return false
    }
  }, [query.data, createTableClearParams, clearMetadataMutation, handleRefetchAfterClear])

  // Helper function to clear column metadata
  const clearColumnMetadata = React.useCallback(async () => {
    const clearParams = createColumnClearParams()
    const result = await clearMetadataMutation.mutateAsync(clearParams)

    if (result) {
      await handleRefetchAfterClear()
      toast.success('All metadata cleared successfully')
      return true
    } else {
      toast.error('Failed to clear metadata')
      console.error('Clear failed:', result)
      return false
    }
  }, [createColumnClearParams, clearMetadataMutation, handleRefetchAfterClear])

  const handleClearAllMetadata = React.useCallback(async () => {
    setIsClearingMetadata(true)

    try {
      if (type === 'table') {
        await clearTableMetadata()
      } else {
        await clearColumnMetadata()
      }
    } catch (error) {
      toast.error('Error clearing metadata')
      console.error('Error clearing metadata:', error)
    } finally {
      setIsClearingMetadata(false)
    }
  }, [type, clearTableMetadata, clearColumnMetadata])

  return {
    handleEnrich,
    handleSave,
    handleCancel,
    handleUndoEnrichment,
    handleRedoEnrichment,
    handleClearAllMetadata,
    isSavingMetadata: saveMetadata.isSaving,
    isClearingMetadata,
  }
}
